/**
 * Simplified extension messaging module
 * Updated for direct messaging approach (v3.0)
 * 
 * This module only handles incoming messages from the extension.
 * No complex bidirectional communication or message tracking needed.
 */

import type {
  ExtensionJobData,
  JobsErrorHandler,
  ChunkProgressData,
  ExtensionErrorData,
  JobsReceivedHandler,
  ChunkProgressHandler,
  ExtensionServiceConfig,
} from './types';

// Event handlers storage
const jobsReceivedHandlers = new Set<JobsReceivedHandler>();
const jobsErrorHandlers = new Set<JobsErrorHandler>();
const chunkProgressHandlers = new Set<ChunkProgressHandler>();

// Configuration
let config: ExtensionServiceConfig = {
  debug: false,
  autoNavigate: false
};

/**
 * Initialize the simplified messaging system
 */
export function initializeMessaging(options: ExtensionServiceConfig = {}): void {
  config = { ...config, ...options };
  
  if (config.debug) {
    console.log('[Extension Messaging] Initializing simplified messaging system');
  }

  // Set up event listeners for extension jobs
  if (typeof window !== 'undefined') {
    // Listen for jobs processed event
    window.addEventListener('extension-jobs-processed', handleJobsProcessedEvent);
    
    // Listen for jobs error event  
    window.addEventListener('extension-jobs-processed-error', handleJobsErrorEvent);
    
    // Listen for chunk progress events
    window.addEventListener('extension-jobs-chunk-progress', handleChunkProgressEvent);

    // Listen for extension sign-out requests
    window.addEventListener('extension-sign-out-request', handleExtensionSignOutRequest);
    
    if (config.debug) {
      console.log('[Extension Messaging] Event listeners registered');
    }
  }
}

/**
 * Handle extension jobs processed event
 */
function handleJobsProcessedEvent(event: Event): void {
  try {
    const data = (event as CustomEvent).detail as ExtensionJobData;
    
    if (config.debug) {
      console.log('[Extension Messaging] Received jobs from extension:', {
        jobCount: data.jobs?.length || 0,
        source: data.source,
        timestamp: data.timestamp
      });
      
      // Print the actual jobs data
      if (data.jobs && data.jobs.length > 0) {
        console.log('[Extension Messaging] Jobs data:', data.jobs);
      }
    }

    // Mark that we received jobs from extension
    if (typeof window !== 'undefined') {
      window.extensionJobsReceived = true;
    }

    // Notify all registered handlers
    jobsReceivedHandlers.forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        console.error('[Extension Messaging] Error in jobs received handler:', error);
      }
    });

  } catch (error) {
    console.error('[Extension Messaging] Error processing jobs event:', error);
  }
}

/**
 * Handle extension jobs error event
 */
function handleJobsErrorEvent(event: Event): void {
  try {
    const data = (event as CustomEvent).detail as ExtensionErrorData;
    
    if (config.debug) {
      console.log('[Extension Messaging] Received error from extension:', data);
    }

    // Notify all registered error handlers
    jobsErrorHandlers.forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        console.error('[Extension Messaging] Error in jobs error handler:', error);
      }
    });

  } catch (error) {
    console.error('[Extension Messaging] Error processing error event:', error);
  }
}

/**
 * Handle extension sign-out request
 * This is called when the extension asks us to sign out the user
 */
function handleExtensionSignOutRequest(event: Event): void {
  try {
    const data = (event as CustomEvent).detail;
    
    if (config.debug) {
      console.log('[Extension Messaging] Received sign-out request from extension:', data);
    }

    // Try to access the useAuth logout function
    // We'll make the logout function globally available for the extension
    if ((window as any).jobJourneyLogout && typeof (window as any).jobJourneyLogout === 'function') {
      console.log('[Extension Messaging] Triggering JobJourney logout');
      (window as any).jobJourneyLogout();
    } else {
      console.warn('[Extension Messaging] JobJourney logout function not available globally');
      
      // Fallback: try to clear localStorage directly (same as useAuth logout does)
      try {
        // Clear auth from localStorage (matching the useAuth logout implementation)
        localStorage.removeItem('auth');
        localStorage.removeItem('authToken');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('userData');
        
        // Redirect to home page
        if (window.location.pathname !== '/') {
          window.location.href = '/';
        } else {
          // If already on home page, reload to clear any cached auth state
          window.location.reload();
        }
        
        console.log('[Extension Messaging] Fallback logout completed');
      } catch (fallbackError) {
        console.error('[Extension Messaging] Fallback logout failed:', fallbackError);
      }
    }

  } catch (error) {
    console.error('[Extension Messaging] Error processing sign-out request:', error);
  }
}

/**
 * Handle chunk progress event from content script
 */
function handleChunkProgressEvent(event: Event): void {
  try {
    const data = (event as CustomEvent).detail as ChunkProgressData;

    if (config.debug) {
      console.log('[Extension Messaging] Chunk progress:', {
        transferId: data.transferId,
        received: data.received,
        totalChunks: data.totalChunks,
        totalJobs: data.totalJobs
      });
    }

    chunkProgressHandlers.forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        console.error('[Extension Messaging] Error in chunk progress handler:', error);
      }
    });
  } catch (error) {
    console.error('[Extension Messaging] Error processing chunk progress event:', error);
  }
}

/**
 * Register a handler for chunk transfer progress updates
 */
export function onChunkProgress(handler: ChunkProgressHandler): () => void {
  chunkProgressHandlers.add(handler);

  if (config.debug) {
    console.log('[Extension Messaging] Registered chunk progress handler. Total handlers:', chunkProgressHandlers.size);
  }

  return () => {
    chunkProgressHandlers.delete(handler);
    if (config.debug) {
      console.log('[Extension Messaging] Unregistered chunk progress handler');
    }
  };
}

/**
 * Register a handler for when jobs are received from extension
 */
export function onJobsReceived(handler: JobsReceivedHandler): () => void {
  jobsReceivedHandlers.add(handler);
  
  if (config.debug) {
    console.log('[Extension Messaging] Registered jobs received handler. Total handlers:', jobsReceivedHandlers.size);
  }

  // Return unsubscribe function
  return () => {
    jobsReceivedHandlers.delete(handler);
    if (config.debug) {
      console.log('[Extension Messaging] Unregistered jobs received handler');
    }
  };
}

/**
 * Register a handler for when extension errors occur
 */
export function onJobsError(handler: JobsErrorHandler): () => void {
  jobsErrorHandlers.add(handler);
  
  if (config.debug) {
    console.log('[Extension Messaging] Registered jobs error handler. Total handlers:', jobsErrorHandlers.size);
  }

  // Return unsubscribe function
  return () => {
    jobsErrorHandlers.delete(handler);
    if (config.debug) {
      console.log('[Extension Messaging] Unregistered jobs error handler');
    }
  };
}

/**
 * Check if extension jobs have been received
 */
export function hasReceivedExtensionJobs(): boolean {
  return typeof window !== 'undefined' && window.extensionJobsReceived === true;
}

/**
 * Clear the extension jobs received flag
 */
export function clearExtensionJobsFlag(): void {
  if (typeof window !== 'undefined') {
    window.extensionJobsReceived = false;
  }
}

/**
 * Cleanup - remove event listeners
 */
export function cleanup(): void {
  if (typeof window !== 'undefined') {
    window.removeEventListener('extension-jobs-processed', handleJobsProcessedEvent);
    window.removeEventListener('extension-jobs-processed-error', handleJobsErrorEvent);
    window.removeEventListener('extension-jobs-chunk-progress', handleChunkProgressEvent);
    window.removeEventListener('extension-sign-out-request', handleExtensionSignOutRequest);
  }

  jobsReceivedHandlers.clear();
  jobsErrorHandlers.clear();
  chunkProgressHandlers.clear();
  
  if (config.debug) {
    console.log('[Extension Messaging] Cleanup completed');
  }
}